import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export type FilterOperator = 'equals' | 'not_equals' | 'in' | 'not_in' | 'greater_than' | 'less_than' | 'between';
export type FilterField =
  | 'subscription_status'
  | 'current_package'
  | 'last_login_days_ago'
  | 'registration_date'
  | 'has_marketing_consent'
  | 'has_ever_subscribed'
  | 'has_ever_paid'
  | 'last_subscription_status';

export interface FilterCondition {
  field: FilterField;
  operator: FilterOperator;
  value: unknown;
}

export interface FilterCriteria {
  operator: 'AND' | 'OR';
  conditions: FilterCondition[];
}

export interface MarketingSegment {
  id: number;
  name: string;
  description: string | null;
  filter_criteria: FilterCriteria;
  estimated_count: number | null;
  last_run_at: string | null;
  manual_user_ids: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
  _count: { campaigns: number };
}

export interface SegmentPreviewUser {
  id: number;
  cognito_id: string;
  subscription_status: string | null;
  marketing_consent: boolean;
  created_at: string;
  subscription_end_date: string | null;
  last_activity: string | null;
  email: string | null;
  display_name: string | null;
}

export interface SegmentPreviewResult {
  count: number;
  sample: SegmentPreviewUser[];
}

export interface MarketingSegmentDraft {
  name?: string;
  description?: string | null;
  filter_criteria?: FilterCriteria;
  manual_user_ids?: string[];
}

function useInvalidateSegments() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['marketing-segments'] });
}

export function useMarketingSegments() {
  return useQuery({
    queryKey: ['marketing-segments'],
    queryFn: async () => (await apiClient<{ segments: MarketingSegment[] }>('/marketing/segments')).segments,
  });
}

export function useCreateMarketingSegment() {
  const invalidate = useInvalidateSegments();
  return useMutation({
    mutationFn: async (draft: MarketingSegmentDraft) =>
      (await apiClient<{ segment: MarketingSegment }>('/marketing/segments', { method: 'POST', body: JSON.stringify(draft) })).segment,
    onSuccess: invalidate,
  });
}

export function useUpdateMarketingSegment() {
  const invalidate = useInvalidateSegments();
  return useMutation({
    mutationFn: async ({ id, ...fields }: MarketingSegmentDraft & { id: number }) =>
      (await apiClient<{ segment: MarketingSegment }>(`/marketing/segments/${id}`, { method: 'PUT', body: JSON.stringify(fields) })).segment,
    onSuccess: invalidate,
  });
}

export function useDeleteMarketingSegment() {
  const invalidate = useInvalidateSegments();
  return useMutation({
    mutationFn: (id: number) => apiClient<{ success: boolean }>(`/marketing/segments/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function usePreviewMarketingSegment() {
  const invalidate = useInvalidateSegments();
  return useMutation({
    mutationFn: (id: number) => apiClient<SegmentPreviewResult>(`/marketing/segments/${id}/preview`, { method: 'POST' }),
    onSuccess: invalidate, // preview persists estimated_count/last_run_at server-side
  });
}

export interface SearchedUser {
  cognito_id: string;
  email: string | null;
  display_name: string | null;
  subscription_status: string | null;
}

// Resolves the cognito ids a segment holds into people. The picker showed the
// raw ids, which are meaningless - you could not tell who was in a segment, or
// which of them to remove. The endpoint existed for this and was never called.
export function useResolveMarketingUsers(cognitoIds: string[]) {
  const key = [...cognitoIds].sort().join(',');
  return useQuery({
    queryKey: ['marketing-users-resolve', key],
    queryFn: async () =>
      (await apiClient<{ users: SearchedUser[] }>('/marketing/users/resolve', {
        method: 'POST',
        body: JSON.stringify({ cognito_ids: cognitoIds }),
      })).users,
    enabled: cognitoIds.length > 0,
  });
}

export function useSearchMarketingUsers(query: string) {
  return useQuery({
    queryKey: ['marketing-users-search', query],
    queryFn: async () => (await apiClient<{ users: SearchedUser[] }>(`/marketing/users/search?q=${encodeURIComponent(query)}`)).users,
    enabled: query.trim().length >= 2,
  });
}
